import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../design-system';
import { auth, onAuthStateChanged, resendVerificationEmail } from '../services/auth.service';
import { User } from 'firebase/auth';
import './EmailVerification.css';

const EmailVerification: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [email, setEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    const storedEmail = localStorage.getItem('verificationEmail');

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser && !storedEmail) {
        navigate('/login');
        return;
      }

      if (currentUser) {
        setUser(currentUser);
        setEmail(currentUser.email || '');

        if (currentUser.emailVerified) {
          navigate('/projects');
        }
      } else if (storedEmail) {
        setEmail(storedEmail);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleResendEmail = async () => {
    if (!user || countdown > 0) return;

    setIsResending(true);
    setMessage('');

    const result = await resendVerificationEmail();

    if (result.success) {
      setMessage('Verification email sent successfully!');
      setCountdown(60);
    } else {
      setMessage(result.error || 'Failed to send email. Please try again.');
    }

    setTimeout(() => setMessage(''), 3000);
    setIsResending(false);
  };

  const handleCheckVerification = async () => {
    if (user) {
      await user.reload();
      if (user.emailVerified) {
        localStorage.removeItem('verificationEmail');
        navigate('/projects');
      } else {
        setMessage('Email not verified yet. Please check your inbox.');
        setTimeout(() => setMessage(''), 3000);
      }
    }
  };

  const handleBackToSignup = () => {
    localStorage.removeItem('verificationEmail');
    navigate('/login');
  };

  return (
    <div className="EmailVerification">
      <div className="EmailVerification__container">
        <div className="EmailVerification__content">
          <div className="EmailVerification__icon">
            <img
              src="/assets/auth/email-inbox.png"
              alt=""
              className="EmailVerification__inbox-image"
              onLoad={() => setImageLoaded(true)}
              style={{ opacity: imageLoaded ? 1 : 0 }}
            />
          </div>

          <div className="EmailVerification__header">
            <h1 className="EmailVerification__title">We sent you a very important message</h1>
            <p className="EmailVerification__subtitle">We sent a verification link to</p>
            <div className="EmailVerification__email">{email}</div>
          </div>

          <p className="EmailVerification__instructions">
            If you don't see it, check your Spam folder.
          </p>

          {message && (
            <div className={`EmailVerification__message ${message.includes('Failed') || message.includes('not verified') ? 'EmailVerification__message--error' : 'EmailVerification__message--success'}`}>
              {message}
            </div>
          )}

          <div className="EmailVerification__actions">
            <Button
              variant="primary"
              size="md"
              fullWidth
              onClick={handleCheckVerification}
            >
              I've verified my email
            </Button>

            {user && (
              <Button
                variant="secondary"
                size="md"
                fullWidth
                onClick={handleResendEmail}
                disabled={isResending || countdown > 0}
              >
                {isResending ? 'Sending...' : countdown > 0 ? `Resend in ${countdown}s` : 'Resend email'}
              </Button>
            )}
          </div>

          <div className="EmailVerification__footer">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToSignup}
            >
              ← Back to login
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
